import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import API from "../../Network/API";
import { ADD_ISSUE_SUCCESS, EDIT_ISSUE_SUCCESS } from "../../Reducer/IssueSlice";

const MAX_WORDS = 50;

const emptyForm = () => ({
    title: "",
    remark: "",
    note: "",
    status: "0",
    solve_at: "",
});

const countWords = (text) =>
    String(text || "")
        .trim()
        .split(/\s+/)
        .filter((w) => w.length > 0).length;

export default function IssueFormModal({ mode = "create", issue = null, isModalVisible, handleShowB }) {
    const dispatch = useDispatch();
    const isEdit = mode === "edit";
    const isAdmin = !!issue?.is_admin;

    const [form, setForm] = useState(emptyForm());
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isModalVisible) return;
        if (isEdit && issue) {
            setForm({
                title: issue.title ?? "",
                remark: issue.remark ?? "",
                note: issue.note ?? "",
                status: String(issue.status ?? 0),
                solve_at: issue.solve_at_input ?? "",
            });
            return;
        }
        setForm(emptyForm());
    }, [isModalVisible, isEdit, issue]);

    const remainingWords = Math.max(0, MAX_WORDS - countWords(form.remark));

    const updateField = (name, value) => {
        if (name === "remark") {
            const words = value.trim().split(/\s+/).filter((w) => w.length > 0);
            if (words.length > MAX_WORDS) {
                value = words.slice(0, MAX_WORDS).join(" ");
            }
        }
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!form.title.trim() || !form.remark.trim()) {
            toast.error("Title and description are required");
            return;
        }

        setLoading(true);
        const payload = {
            title: form.title,
            remark: form.remark,
        };

        if (isEdit && isAdmin) {
            payload.status = form.status;
            payload.note = form.note;
            if (form.status === "1") {
                payload.solve_at = form.solve_at || "";
            }
        }

        const request = isEdit
            ? API.post(`/v1/oauth/issues/${issue.id}/update`, payload)
            : API.post("/v1/oauth/issues/store", payload);

        request
            .then((response) => {
                setLoading(false);
                toast.success(response.data?.message ?? "Saved");
                if (isEdit) {
                    dispatch(EDIT_ISSUE_SUCCESS(response.data.data));
                } else {
                    dispatch(ADD_ISSUE_SUCCESS(response.data.data));
                }
                handleShowB();
            })
            .catch((error) => {
                setLoading(false);
                const message = error.response?.data?.message ?? "Request failed";
                toast.error(typeof message === "string" ? message : "Validation failed");
            });
    };

    return (
        <Modal
            isOpen={isModalVisible}
            toggle={handleShowB}
            className="modal-dialog-scrollable"
            backdrop="static"
        >
            <ModalHeader toggle={handleShowB}>
                {isEdit ? "Edit Issue" : "Add Issue"}
            </ModalHeader>
            <ModalBody className="bg-white">
                <form onSubmit={handleSubmit} id="issue_form_modal">
                    <div className="form-group">
                        <label className="control-label">Title<em className="text-danger">*</em></label>
                        <input
                            className="form-control"
                            required
                            name="title"
                            type="text"
                            placeholder="Start from here"
                            value={form.title}
                            onChange={(e) => updateField("title", e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label className="control-label">Description<em className="text-danger">*</em></label>
                        <textarea
                            className="form-control"
                            required
                            name="remark"
                            rows={4}
                            placeholder="Enter the description"
                            value={form.remark}
                            onChange={(e) => updateField("remark", e.target.value)}
                        />
                        <div className="text-danger small mt-1">{remainingWords} words remaining</div>
                    </div>

                    {isEdit && isAdmin && (
                        <>
                            <div className="form-group">
                                <label className="control-label">Status</label>
                                <select
                                    className="form-select"
                                    name="status"
                                    value={form.status}
                                    onChange={(e) => updateField("status", e.target.value)}
                                >
                                    <option value="0">Open</option>
                                    <option value="2">Progress</option>
                                    <option value="1">Close</option>
                                    <option value="3">Cancel</option>
                                    <option value="4">Re-open</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="control-label">Feedback</label>
                                <textarea
                                    className="form-control"
                                    name="note"
                                    rows={3}
                                    placeholder="Admin feedback"
                                    value={form.note}
                                    onChange={(e) => updateField("note", e.target.value)}
                                />
                            </div>

                            {form.status === "1" && (
                                <div className="form-group">
                                    <label className="control-label">Solve At</label>
                                    <input
                                        type="datetime-local"
                                        className="form-control"
                                        name="solve_at"
                                        value={form.solve_at}
                                        onChange={(e) => updateField("solve_at", e.target.value)}
                                    />
                                </div>
                            )}
                        </>
                    )}

                    <ModalFooter className="post-footer-comment-icon bg-light px-0">
                        <button type="button" className="btn btn-secondary" onClick={handleShowB}>
                            Close
                        </button>
                        <button type="submit" className="btn btn-success rounded-pill" disabled={loading}>
                            <i className="fa-light fa-save me-1" />
                            {isEdit ? "Update" : "Save"}
                            {loading && (
                                <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />
                            )}
                        </button>
                    </ModalFooter>
                </form>
            </ModalBody>
        </Modal>
    );
}
